/**
 * TanStack Query client configuration.
 * See ARCHITECTURE.md §10 for caching/stale-time defaults.
 */

import { QueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data considered fresh for 5 minutes — avoids refetches on mount.
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 30 minutes.
      gcTime: 30 * 60 * 1000,
      // Retry failed queries up to 2 times with exponential backoff.
      retry: (failureCount, error) => {
        // Don't retry 4xx errors except 429
        if (error instanceof Error && 'status' in error) {
          const status = (error as Error & { status: number }).status
          if (status >= 400 && status < 500 && status !== 429) return false
        }
        return failureCount < 2
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus in prod for real-time data freshness.
      refetchOnWindowFocus: import.meta.env.PROD,
    },
    mutations: {
      // Global mutation error handler — toast + Sentry integration added in Phase 2.
      onError: (error: Error) => {
        // Global fallback — individual hooks may override with specific messages
        const msg = error.message || 'Något gick fel. Försök igen.'

        // Rate limit — 429
        if (msg.includes('429') || msg.includes('Too Many') || msg.includes('rate limit')) {
          toast.error('Vänligen försök igen om en stund — för många förfrågningar.')
          return
        }

        // Permission denied
        if (msg.includes('permission') || msg.includes('403') || msg.includes('not allowed')) {
          toast.error('Du har inte behörighet att utföra denna åtgärd.')
        }
      },
    },
  },
})
